import { AlgoliaMapBounds } from '@/pages/AuctionsNearMe/types/AuctionsNearMe.types';
import { allGeoAreasSelector } from '@/redux/modules/geoArea/geoArea.selectors';
import { debounce } from 'es-toolkit';
import {
    defaultAuctionsNearMeUkBounds,
    defaultAuctionsNearMeUsaBounds,
} from '@/pages/AuctionsNearMe/constants/AuctionsNearMeConstants';
import { FF_SG229_NEW_AUCTIONS_NEAR_ME_PAGE } from '@/pages/AuctionsNearMe/constants/featureFlags';
import { FilterChip } from '@liveauctioneers/caterwaul-components/lib/FilterChip/FilterChip';
import { GeoArea } from '@/types/GeoArea';
import { getAuctionsNearMePageUrl, getAuctionsNearMeSearchUrl } from '@/utils/urls';
import { getGeocoder } from './helpers/getGeocoder';
import { ReactElement, useState } from 'react';
import { useAppSelector } from '@/redux/hooks';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useNavigate } from 'react-router-dom';
import SearchInput from '@liveauctioneers/caterwaul-components/lib/Input/SearchInput/SearchInput';
import styled from 'styled-components';

export type AuctionsNearMeSearchInputProps = {
    currentGeoArea: GeoArea | undefined | null;
    onSearchResults: (bounds: AlgoliaMapBounds) => void;
    setGeocoderFailed: (failed: boolean) => void;
    setPageTitleSearchTerm: (term: string) => void;
};

export const AuctionsNearMeSearchInput = ({
    currentGeoArea,
    onSearchResults,
    setGeocoderFailed,
    setPageTitleSearchTerm,
}: AuctionsNearMeSearchInputProps): ReactElement => {
    const useNewUrlFormat = FF_SG229_NEW_AUCTIONS_NEAR_ME_PAGE;
    const google = typeof window !== 'undefined' ? window.google : null;
    const navigate = useNavigate();
    const { formatMessage, locale } = useIntl();
    const placeholder = formatMessage({
        id: 'auctionsNearMe.searchLocationPlaceholder',
    });
    const allGeoAreas = useAppSelector(allGeoAreasSelector);
    const geoAreaOptions = allGeoAreas.map((area) => area.geoAreaName);
    const [searchTerm, setSearchTerm] = useState('');
    const [inputValue, setInputValue] = useState('');

    const provideBoundsViaGeocoder = (selectedOption: string): void => {
        const geocoder = getGeocoder(google);
        geocoder.geocode({ address: selectedOption }, (results, status) => {
            if (status !== google.maps.GeocoderStatus.OK || results.length === 0) {
                setGeocoderFailed(true);
                // Do not navigate on failure - stay on current page
                return;
            }
            const { viewport } = results[0].geometry;

            if (!viewport) {
                setGeocoderFailed(true);
                return;
            }

            const ne = viewport.getNorthEast();
            const sw = viewport.getSouthWest();

            const northEast = {
                lat: ne.lat(),
                lng: ne.lng(),
            };
            const southWest = {
                lat: sw.lat(),
                lng: sw.lng(),
            };

            // Geocoding succeeded
            setGeocoderFailed(false);
            onSearchResults({
                northEast,
                southWest,
            });

            // Navigate to custom search URL (only for new feature flag)
            if (useNewUrlFormat) {
                const searchUrl = getAuctionsNearMeSearchUrl(selectedOption);
                navigate(searchUrl);
            }
        });
    };

    const provideBoundsViaGeoArea = (selectedOption: string): void => {
        const geoArea = allGeoAreas.find((geoArea) => geoArea.geoAreaName.toString() === selectedOption);

        if (!geoArea) {
            return;
        }

        const { northEastLatitude, northEastLongitude, southWestLatitude, southWestLongitude } = geoArea;
        const northEast = {
            lat: northEastLatitude,
            lng: northEastLongitude,
        };
        const southWest = {
            lat: southWestLatitude,
            lng: southWestLongitude,
        };
        setGeocoderFailed(false);
        onSearchResults({
            northEast,
            southWest,
        });

        // Use new URL format with GeoArea object
        const url = getAuctionsNearMePageUrl(geoArea, useNewUrlFormat);
        navigate(url);
    };

    const provideBoundsViaDefault = (): void => {
        const defaultBounds = locale === 'en-gb' ? defaultAuctionsNearMeUkBounds : defaultAuctionsNearMeUsaBounds;
        setGeocoderFailed(false);
        onSearchResults(defaultBounds);
    };

    const onChange = (event): void => {
        setInputValue(event.target.value);
    };

    const onSubmit = (selectedOption: string): void => {
        if (!google) {
            return;
        }
        setSearchTerm(selectedOption);
        setPageTitleSearchTerm(selectedOption);
        if (geoAreaOptions.includes(selectedOption)) {
            provideBoundsViaGeoArea(selectedOption);
        } else if (Boolean(selectedOption)) {
            provideBoundsViaGeocoder(selectedOption);
        } else {
            provideBoundsViaDefault();
        }
    };

    const navigateToMainPage = () => {
        setInputValue('');
        setSearchTerm('');
        setPageTitleSearchTerm('');
        setGeocoderFailed(false);
        provideBoundsViaDefault();
        navigate(getAuctionsNearMePageUrl());
    };
    const currentSearchTerm = searchTerm || currentGeoArea?.geoAreaName;
    return (
        <StyledSearchBar>
            <StyledInputContainer>
                <SearchInput
                    aria-label={formatMessage({
                        id: 'auctionsNearMe.ariaLabel.searchInput',
                    })}
                    controlled
                    hideButton
                    onChange={onChange}
                    onSubmit={debounce(onSubmit, 300)}
                    options={geoAreaOptions}
                    placeholder={placeholder}
                    small
                    value={inputValue}
                    wide
                />
            </StyledInputContainer>
            {Boolean(currentSearchTerm) && (
                <StyledChip>
                    <FilterChip onRemove={navigateToMainPage}>{currentSearchTerm}</FilterChip>
                </StyledChip>
            )}
        </StyledSearchBar>
    );
};

export default AuctionsNearMeSearchInput;

const StyledSearchBar = styled.section`
    display: flex;
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        flex-direction: column;
    }
    @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidthNum + 1}px) {
        flex-direction: row;
    }
`;

const StyledInputContainer = styled.span`
    margin-right: 8px;
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        width: 100%;
    }
    @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidthNum + 1}px) {
        width: 184px;
    }
`;

const StyledChip = styled.span`
    [class^='FilterChip__StyledFilterChip'] {
        @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
            margin-right: 0;
            margin-bottom: 8px;
            margin-top: -8px;
        }
    }
`;
